import { FaRegEdit, FaSearch } from "react-icons/fa";
import { MdDeleteOutline } from "react-icons/md";
import { FiFilter } from "react-icons/fi";
import { RxCross2 } from "react-icons/rx";
import { MdAdd } from "react-icons/md";
import "../commonStyle.css";
import {
  CAvatar,
  CBadge,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import CustomPagination from "../customPagination";
import CustomModal from "../customModal";
import ModalV2 from "../customModal/ModalV2";
import DefaultProfilePic from "../../assets/images/DefaultProfilePic.jpg";
import { UPDATE_ARTICLES_LIST, UPDATE_ARTICLES_LIST_DATA } from "../../store/actionTypes";
import { ARTICLE_STATUS } from "../../utils/contentTypes";
import ROUTE_NAMES from "../../routes/routeNames";
import { deleteArticle, getArticlesList } from "../../views/articles/action";
import { canUserEditArticle, getDateAndTimeFormatted } from "../../utils/commonFunctions";
import { BsSortAlphaDown, BsSortAlphaDownAlt } from "react-icons/bs";

const getStatusMeta = (status) => {
  if (status === ARTICLE_STATUS.DRAFT) {
    return { label: "Draft", color: "warning" };
  }

  if (status === ARTICLE_STATUS.PUBLISHED) {
    return { label: "Published", color: "success" };
  }

  return { label: "Unknown", color: "secondary" };
};

const ArticlesTable = () => {
  const {
    articlesList,
    pageNo,
    totalPage,
    limit,
    searchKey,
    isLoading,
    status,
    sortOrder,
    sortBy
  } = useSelector((state) => state.ArticleReducer);
  const authReducer = useSelector((state) => state.AuthReducer);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState(searchKey);
  const [filterModal, setFilterModal] = useState(false);
  const [localStatus, setLocalStatus] = useState(status);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedArticleId, setSelectedArticleId] = useState(null);
  const [selectedArticleTitle, setSelectedArticleTitle] = useState("");
  const [sortApplied, setSortApplied] = useState(sortBy);

  const updatePage = (page) => {
    dispatch({
      type: UPDATE_ARTICLES_LIST,
      payload: { pageNo: page },
    });
  };

  const pageLimitHandler = (e) => {
    const currLimit = e?.target?.value;
    dispatch({
      type: UPDATE_ARTICLES_LIST,
      payload: { limit: currLimit, pageNo: 1 },
    });
  };

  const handleDebounceFn = (input) => {
    dispatch({
      type: UPDATE_ARTICLES_LIST,
      payload: { searchKey: input, pageNo: 1 },
    });
  };

  const deBounceFn = useMemo(() => debounce(handleDebounceFn, 800), []);

  const handleChange = (e) => {
    const text = e.target.value.trimStart();
    setInputValue(text);
    if (text.length === 0 || text.length > 2) {
      deBounceFn(text);
    }
  };

  const handleClearInputField = () => {
    setInputValue("");
    deBounceFn("");
  };

  const handleFilter = (newStatus) => {
    setFilterModal(false);
    dispatch({
      type: UPDATE_ARTICLES_LIST,
      payload: { pageNo: 1, status: newStatus },
    });
  };

  const handleResetFilter = () => {
    setLocalStatus("");
    handleFilter("");
  };

  const openDeleteModal = (article) => {
    setSelectedArticleId(article?._id);
    setSelectedArticleTitle(article?.title || "this article");
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedArticleId(null);
    setSelectedArticleTitle("");
  };

  const handleConfirmDelete = () => {
    if (!selectedArticleId) return;

    dispatch(
      deleteArticle(selectedArticleId, (response) => {
        if (response.isSuccess) {
          closeDeleteModal();
          dispatch(getArticlesList());
        }
      }),
    );
  };

  const handlerSort = (sortBy) => {
    setSortApplied(sortBy);
    dispatch({
      type: UPDATE_ARTICLES_LIST,
      payload: { sortBy: sortBy, sortOrder: sortApplied === sortBy ? (sortOrder === 1 ? -1 : 1) : 1 },
    });
  };

  const renderTableBody = () => {
    if (isLoading) {
      return (
        <CTableBody>
          <CTableRow>
            <CTableDataCell colSpan={7} className="text-center py-5">
              <CSpinner color="primary" />
              <div className="mt-2">Fetching articles...</div>
            </CTableDataCell>
          </CTableRow>
        </CTableBody>
      );
    }

    if (!articlesList?.length) {
      return (
        <CTableBody>
          <CTableRow>
            <CTableDataCell colSpan={7} className="text-center py-5 text-muted">
              No articles available.
            </CTableDataCell>
          </CTableRow>
        </CTableBody>
      );
    }

    return (
      <CTableBody>
        {articlesList.map((article, index) => {
          const statusMeta = getStatusMeta(article?.status);
          const title = article?.title || "N/A";

          return (
            <CTableRow key={article?._id || index} className="table-data-text">
              <CTableDataCell className="text-center">
                {(pageNo - 1) * limit + index + 1}
              </CTableDataCell>

              <CTableDataCell title={title}>
                {title.length > 50 ? `${title.slice(0, 50)}...` : title}
              </CTableDataCell>

              <CTableDataCell>
                <b>{article?.readTime ? `${article.readTime} min.` : "N/A"}</b>
              </CTableDataCell>

              <CTableDataCell>
                {article?.createdAt
                  ? getDateAndTimeFormatted(article.createdAt)
                  : "N/A"}
              </CTableDataCell>

              <CTableDataCell>
                {article?.updatedAt
                  ? getDateAndTimeFormatted(article.updatedAt)
                  : "N/A"}
              </CTableDataCell>

              <CTableDataCell>
                <CBadge color={statusMeta.color}>{statusMeta.label}</CBadge>
              </CTableDataCell>

              <CTableDataCell>
                <FaRegEdit
                  title="Edit Article"
                  className={`actions-icons cursor-pointer`}
                  onClick={() =>
                    navigate(
                      ROUTE_NAMES.MAIN_ROUTES.VIEW_EDIT_ARTICLES.replace(
                        ":articleId",
                        article?._id,
                      ),
                    )
                  }
                />
                <MdDeleteOutline
                  title="Delete Article"
                  className="actions-icons cursor-pointer ms-2"
                  onClick={() => openDeleteModal(article)}
                  fill="red"
                />
              </CTableDataCell>
            </CTableRow>
          );
        })}
      </CTableBody>
    );
  };

  const renderPagination = () => {
    return (
      <CustomPagination
        limit={limit}
        limitHanlder={pageLimitHandler}
        pageNo={pageNo}
        pageChangeHanlder={updatePage}
        totalPage={totalPage}
      />
    );
  };

  const renderFilterModalBody = () => {
    return (
      <div className="filter-modal-body-container">
        <div className="info-sub-child-container">
          <label className="lable-text" htmlFor="articleStatus">
            {"Article Status"}
          </label>
          <select
            name="articleStatus"
            id="articleStatus"
            value={localStatus}
            onChange={(e) => setLocalStatus(e.target.value)}
            className="form-select form-select-md"
            aria-label="Select Article Status"
          >
            <option value="">{"Show All"}</option>
            <option value={ARTICLE_STATUS.DRAFT}>{"Draft"}</option>
            <option value={ARTICLE_STATUS.PUBLISHED}>{"Published"}</option>
          </select>
        </div>

        <div className="d-flex justify-content-end mt-3">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={handleResetFilter}
          >
            {"Reset"}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <CustomModal
        visible={filterModal}
        visibleHandler={() => {
          setFilterModal(false);
          setLocalStatus(status);
        }}
        heading={"Article Status"}
        body={renderFilterModalBody}
        confirmHandler={() => handleFilter(localStatus)}
        confirmText={"Apply"}
      />

      <div className="table-header-container">
        <h3 className="w-50 mb-0 text-primary">{"Articles List"}</h3>
        <div className="d-flex justify-content-end align-items-center gap-1">
          <div
            className={`btn ${status ? "btn-primary" : "btn-outline-primary"} btn-sm add-btn add-btn-container`}
            onClick={() => {
              setLocalStatus(status);
              setFilterModal(true);
            }}
          >
            <span className="px-2">{"Filter"}</span>
            <span className="add-icon">
              <FiFilter />
            </span>
          </div>

          <CInputGroup className="" style={{ width: "300px" }} size="sm">
            <CInputGroupText className="cursor-pointer">
              <FaSearch />
            </CInputGroupText>
            <CFormInput
              value={inputValue}
              name="text"
              onChange={handleChange}
              placeholder="Search article title"
            />

            {inputValue && (
              <CInputGroupText
                className="cursor-pointer"
                onClick={handleClearInputField}
              >
                <RxCross2 />
              </CInputGroupText>
            )}
          </CInputGroup>

          <div
            className="btn btn-primary btn-sm add-btn add-btn-container"
            onClick={() => navigate("/articles/add")}
          >
            <span>{"Add"}</span>
            <span className="add-icon">
              <MdAdd />
            </span>
          </div>
        </div>
      </div>

      <CTable hover responsive>
        <CTableHead className="text-nowrap border">
          <CTableRow>
            <CTableHeaderCell className="bg-body-tertiary text-center table-heading-10-cell">
              S. No.
            </CTableHeaderCell>
            <CTableHeaderCell className={`bg-body-tertiary table-heading-30-cell ${sortApplied === "title" ? "sort-highlight" : ""}`}>
              Title
              <span onClick={() => handlerSort("title")}>
                  {sortOrder === 1 ? (
                    <BsSortAlphaDown className="sort-icons cursor-pointer" />
                  ) : (
                    <BsSortAlphaDownAlt className="sort-icons cursor-pointer" />
                  )}
                </span>
            </CTableHeaderCell>
            <CTableHeaderCell className={`bg-body-tertiary table-heading-15-cell ${sortApplied === "readTime" ? "sort-highlight" : ""}`}>
              Read Time
              <span onClick={() => handlerSort("readTime")}>
                  {sortOrder === 1 ? (
                    <BsSortAlphaDown className="sort-icons cursor-pointer" />
                  ) : (
                    <BsSortAlphaDownAlt className="sort-icons cursor-pointer" />
                  )}
                </span>
            </CTableHeaderCell>
            <CTableHeaderCell className={`bg-body-tertiary table-heading-15-cell ${sortApplied === "createdAt" ? "sort-highlight" : ""}`}>
              Created At
              <span onClick={() => handlerSort("createdAt")}>
                  {sortOrder === 1 ? (
                    <BsSortAlphaDown className="sort-icons cursor-pointer" />
                  ) : (
                    <BsSortAlphaDownAlt className="sort-icons cursor-pointer" />
                  )}
                </span>
            </CTableHeaderCell>
              <CTableHeaderCell className={`bg-body-tertiary table-heading-15-cell ${sortApplied === "updatedAt" ? "sort-highlight" : ""}`}>
              Updated At
              <span onClick={() => handlerSort("updatedAt")}>
                  {sortOrder === 1 ? (
                    <BsSortAlphaDown className="sort-icons cursor-pointer" />
                  ) : (
                    <BsSortAlphaDownAlt className="sort-icons cursor-pointer" />
                  )}
              </span>
            </CTableHeaderCell>
            <CTableHeaderCell className="bg-body-tertiary table-heading-15-cell">
              Status
            </CTableHeaderCell>
            <CTableHeaderCell className="bg-body-tertiary table-heading-15-cell">
              Actions
            </CTableHeaderCell>
          </CTableRow>
        </CTableHead>

        {renderTableBody()}
      </CTable>

      {articlesList?.length ? renderPagination() : null}

      <ModalV2
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Delete Article"
        confirmButtonLabel="Delete"
        cancelButtonLabel="Cancel"
        confirmDisabled={!selectedArticleId}
        size="small"
      >
        <p className="mb-0">
          Are you sure you want to delete{" "}
          <strong>{selectedArticleTitle}</strong>?
        </p>
      </ModalV2>
    </div>
  );
};

export default ArticlesTable;
